import { useState, useRef, useEffect } from 'react';
import { motion, useDragControls } from 'framer-motion';
import { X, BookOpen } from 'lucide-react'; // Import BookOpen
import clsx from 'clsx';
import { useUIContext } from "../providers/UIProvider";

interface DocumentationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  url?: string;
}

const DocumentationPanel: React.FC<DocumentationPanelProps> = ({ isOpen, onClose, url }) => {
  const { isDarkMode } = useUIContext();
  const [width, setWidth] = useState(360); // Default width similar to RightPanel
  const [currentUrl, setCurrentUrl] = useState(url || "https://docs.metarouter.io/docs/getting-started-with-metarouter");
  const panelRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();
  const [hasBeenOpened, setHasBeenOpened] = useState(false); // New state for lazy loading

  // Define variants for panel animation
  const variants = {
    expanded: { opacity: 1, x: 0, pointerEvents: 'auto' as const },
    collapsed: { opacity: 0, x: width + 100, pointerEvents: 'none' as const },
  };

  // Effect to handle initial width and potential resizing
  useEffect(() => {
    // You might want to save/load width from localStorage here if persistence is desired
    // const savedWidth = localStorage.getItem('documentationPanelWidth');
    // if (savedWidth) {
    //   setWidth(parseInt(savedWidth, 10));
    // }
  }, []);

  // Effect to track if the panel has been opened
  useEffect(() => {
    if (isOpen && !hasBeenOpened) {
      setHasBeenOpened(true);
    }
  }, [isOpen, hasBeenOpened]);

  // Effect to update URL when prop changes
  useEffect(() => {
    if (url) {
      setCurrentUrl(url);
    }
  }, [url]);

  return (
    <motion.div
      ref={panelRef}
      className={clsx(
        "fixed top-16 right-0 h-[calc(100vh-56px)] bg-white border-l border-gray-200 flex flex-col z-10",
        isDarkMode && "dark:bg-gray-800 dark:border-gray-700"
      )}
      initial={isOpen ? "expanded" : "collapsed"}
      animate={isOpen ? "expanded" : "collapsed"}
      variants={variants}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      style={{ width: `${width}px` }}
    >
      {/* Header */}
      <div className={clsx(
        "w-full bg-primary-100 dark:bg-gray-700 h-9 px-4 flex justify-between items-center flex-shrink-0",
        isDarkMode && "dark:bg-gray-700"
      )}>
        <span className={clsx("text-blue-900 font-semibold text-sm", isDarkMode && "dark:text-white")}>Documentation</span>
        <button onClick={onClose} className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600">
          <X className={clsx("text-gray-600", isDarkMode && "dark:text-gray-300")} size={16} />
        </button>
      </div>

      {/* Iframe Content */}
      <div className="flex-grow overflow-hidden flex items-center justify-center">
        {hasBeenOpened ? ( // Conditionally render iframe or placeholder
          <iframe
            src={currentUrl}
            title="MetaRouter Documentation"
            className="w-full h-full border-none"
            style={{ backgroundColor: isDarkMode ? '#2d3748' : '#ffffff' }} // Attempt to set iframe background for dark mode
          />
        ) : (
          <BookOpen className={clsx("w-24 h-24 text-gray-300", isDarkMode && "dark:text-gray-600")} />
        )}
      </div>

      {/* Resize handle */}
      <motion.div
        className="absolute left-0 top-0 bottom-0 w-2 group hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors duration-150"
        style={{ cursor: 'default' }}
        onPanStart={(e, _) => dragControls.start(e)}
        onPan={(_, info) => {
          const newWidth = Math.max(360, Math.min(window.innerWidth * 0.5, width - info.delta.x)); // Max 50% of screen width
          setWidth(newWidth);
        }}
        onMouseEnter={(e) => { e.currentTarget.style.cursor = 'col-resize'; }}
        onMouseLeave={(e) => { e.currentTarget.style.cursor = 'default'; }}
      >
        <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-gray-200 dark:bg-gray-700 group-hover:bg-blue-400 dark:group-hover:bg-blue-500 transition-colors duration-150" />
      </motion.div>
    </motion.div>
  );
};

export default DocumentationPanel;
